/**
 * SalesProspectsWidget — persoonlijk sales-overzicht. Toont de prospects
 * (contactpersonen) waarvan de ingelogde gebruiker eigenaar is (match op voornaam,
 * of een override via de widget-instelling):
 *  - Melding-lijst (rood) van prospects waar al >1 week (en >2 weken) geen contact
 *    mee is geweest. Prospects waar nog NOOIT contact mee is geweest tellen niet mee
 *    (nog niet relevant — alleen na eerste contact).
 *  - Lijstje recente prospects.
 *  - Mini-stats (zoals de stats-tab, maar op de persoon).
 * Alleen echte contactpersonen (met naam) tellen; accounts zonder contactpersoon
 * vallen vanzelf weg. Klikken op een regel opent die contactpersoon direct.
 *
 * Databron: GET /Api/sales-prospects (aggregate). Last-contact wordt afgeleid uit
 * contacts + uitgaande mailLog; er is geen voorberekend veld.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CircularProgress, TextField, MenuItem,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon } from './widgetTokens';

const TEAM = ['Joris', 'Vincent', 'Joanne', 'Pepijn', 'Ton', 'Romy', 'Boy'];
const ACTIVE_STAGES = ['nieuw', 'contact', 'gesprek', 'offerte'];
const STAGE_LABEL = {
  nieuw: 'Nieuw', contact: 'Eerste contact', gesprek: 'In gesprek',
  offerte: 'Offerte', klant: 'Klant', archief: 'Archief', afgevallen: 'Afgevallen',
};
const STAGE_COLOR = {
  nieuw: '#767c7e', contact: '#526075', gesprek: '#635983',
  offerte: '#10B981', klant: '#F59E0B', archief: '#556071', afgevallen: '#EF4444',
};
const GENERIC_MAIL = new Set(['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'yahoo.com', 'icloud.com', 'me.com']);

const DAY = 86400000;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); }
  catch { return ''; }
};
const domainOf = (url) => {
  if (!url) return '';
  try { return new URL(/^https?:\/\//.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return ''; }
};
const domainFromEmail = (email) => {
  const d = (email || '').split('@')[1]?.toLowerCase().trim();
  return d && !GENERIC_MAIL.has(d) ? d : '';
};
// Favicon-bron: expliciete logo's eerst, dan domein (prospect → account → mail).
const faviconOf = (p, acc) => {
  if (p?.logo_url) return p.logo_url;
  if (acc?.logo_url) return acc.logo_url;
  const dom = domainOf(p?.website) || domainOf(acc?.website) || domainFromEmail(p?.email);
  return dom ? `https://icons.duckduckgo.com/ip3/${dom}.ico` : null;
};

function Favicon({ src, label, color }) {
  const [err, setErr] = useState(false);
  if (src && !err) {
    return <img src={src} width={22} height={22} alt="" onError={() => setErr(true)}
      style={{ borderRadius: 5, objectFit: 'cover', display: 'block', flexShrink: 0 }} />;
  }
  const letter = (label || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <Box sx={{
      width: 22, height: 22, borderRadius: '5px', flexShrink: 0,
      bgcolor: color || T.outlineVariant, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: T.font, fontSize: '0.68rem', fontWeight: 700,
    }}>
      {letter}
    </Box>
  );
}

function ProspectRow({ p, acc, rightNode, tone, onClick }) {
  const red = tone === 'red';
  return (
    <Box onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
        bgcolor: red ? '#fef2f2' : T.surfaceLow, borderRadius: '0.5rem', px: 1.25, py: 1,
        border: red ? '1px solid #fecaca' : '1px solid transparent',
        transition: 'background 0.15s, filter 0.15s',
        '&:hover': red ? { filter: 'brightness(0.98)' } : { bgcolor: T.surfaceHigh },
      }}>
      <Favicon src={faviconOf(p, acc)} label={acc?.naam || p.bedrijf || p.naam} color={STAGE_COLOR[p.stage]} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 600, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.naam}
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.outline,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(acc?.naam || p.bedrijf || '—')} · {STAGE_LABEL[p.stage] || p.stage}
        </Typography>
      </Box>
      {rightNode}
    </Box>
  );
}

export function SalesProspectsSettings({ settings, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', color: T.text }}>
        Sales-instellingen
      </Typography>
      <TextField
        select size="small" label="Eigenaar" value={settings.eigenaar || 'auto'}
        onChange={(e) => onChange({ eigenaar: e.target.value })}
        helperText="Automatisch = je voornaam"
        sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.82rem' } }}
      >
        <MenuItem value="auto">Automatisch</MenuItem>
        {TEAM.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
      </TextField>
    </Box>
  );
}

const Stat = ({ value, label, color = T.text }) => (
  <Box sx={{ flex: 1, minWidth: 64 }}>
    <Typography sx={{ fontFamily: T.font, fontSize: '1.35rem', fontWeight: 800, color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 600, color: T.outline,
      textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

export default function SalesProspectsWidget({ settings = {} }) {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const userVoornaam = (accounts?.[0]?.name || '').split(' ')[0] || '';
  const eigenaar = (settings.eigenaar && settings.eigenaar !== 'auto') ? settings.eigenaar : userVoornaam;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // compact=1: mailLog zonder volledige mailbodies — de widget gebruikt alleen
        // prospect_id/richting/datum voor last-contact, geen mail-inhoud.
        const r = await authFetch(`${BACKEND_URL}/Api/sales-prospects?compact=1`);
        if (!r.ok) throw new Error('fetch failed');
        const d = await r.json();
        if (active) setData(d);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const accById = {};
    (data.accounts || []).forEach((a) => { accById[a.id] = a; });
    const ownerOf = (p) => (p.eigenaar || accById[p.account_id]?.eigenaar || '').trim();
    const target = eigenaar.toLowerCase();

    // Alleen echte contactpersonen (met naam); accounts zonder contactpersoon
    // leveren geen prospect-rij op en vallen daarmee vanzelf weg.
    const mine = (data.prospects || []).filter((p) =>
      target && (p.naam || '').trim() && ownerOf(p).toLowerCase() === target);

    const lastByProspect = {};
    const push = (id, iso) => {
      if (!id || !iso) return;
      const t = new Date(iso).getTime();
      if (!lastByProspect[id] || t > lastByProspect[id]) lastByProspect[id] = t;
    };
    (data.contacts || []).forEach((c) => push(c.prospect_id, c.datum));
    (data.mailLog || []).forEach((m) => { if (m.richting !== 'inkomend') push(m.prospect_id, m.datum); });
    const lastDays = (p) => {
      const t = lastByProspect[p.id];
      return t ? Math.floor((Date.now() - t) / DAY) : null;
    };

    const active = mine.filter((p) => ACTIVE_STAGES.includes(p.stage));
    // Alleen prospects waar al WEL eerste contact is geweest (d !== null) tellen mee.
    const needsContact = active
      .map((p) => ({ p, acc: accById[p.account_id], d: lastDays(p) }))
      .filter((x) => x.d !== null && x.d > 7)
      .sort((a, b) => b.d - a.d);
    const urgent = needsContact.filter((x) => x.d > 14);

    const klanten = mine.filter((p) => p.stage === 'klant').length;
    const recent = [...mine]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map((p) => ({ p, acc: accById[p.account_id] }));

    return {
      total: mine.length,
      active: active.length,
      klanten,
      conversie: mine.length ? Math.round((klanten / mine.length) * 100) : 0,
      needsContact, urgent, recent,
    };
  }, [data, eigenaar]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
      <CircularProgress size={18} sx={{ color: T.outlineVariant }} />
    </Box>;
  }
  if (error) {
    return <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>
      Kon sales-gegevens niet laden.
    </Typography>;
  }
  if (!view || view.total === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2, px: 1, gap: 1 }}>
        <Icon name="person_search" size={30} sx={{ color: T.outlineVariant }} />
        <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 700, color: T.text }}>
          Geen prospects op {eigenaar ? `naam van ${eigenaar}` : 'jouw naam'}
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline, maxWidth: 280 }}>
          Deze widget is bedoeld voor het sales-team. Voor jou heeft 'ie waarschijnlijk
          weinig nut — of kies via ⚙ een andere eigenaar.
        </Typography>
      </Box>
    );
  }

  const openProspect = (p) => navigate(`/sales/prospects?prospect=${p.id}`);

  const SectionLabel = ({ children, color }) => (
    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 700,
      color: color || T.outline, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
      {children}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Melding-lijst: contact nodig (alleen ná eerste contact) ── */}
      {view.needsContact.length > 0 && (
        <Box>
          <SectionLabel color="#b91c1c">
            {view.needsContact.length} &gt; 1 week geen contact{view.urgent.length > 0 ? ` · ${view.urgent.length} > 2 weken` : ''}
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {view.needsContact.slice(0, 6).map(({ p, acc, d }) => (
              <ProspectRow key={p.id} p={p} acc={acc} tone="red" onClick={() => openProspect(p)}
                rightNode={
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
                    color: d > 14 ? '#b91c1c' : '#d97706', flexShrink: 0 }}>
                    {d}d
                  </Typography>
                } />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Mini-stats ── */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Stat value={view.total} label="Prospects" />
        <Stat value={view.active} label="Actief" color={T.primary} />
        <Stat value={view.klanten} label="Klant" color="#d97706" />
        <Stat value={`${view.conversie}%`} label="Conversie" color={T.emerald} />
      </Box>

      {/* ── Recente prospects ── */}
      <Box>
        <SectionLabel>Recente prospects</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {view.recent.map(({ p, acc }) => (
            <ProspectRow key={p.id} p={p} acc={acc} onClick={() => openProspect(p)}
              rightNode={
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.64rem', color: T.outlineVariant, flexShrink: 0 }}>
                  {fmtDate(p.created_at)}
                </Typography>
              } />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
